<template>
  <div
    class="DetailImageGallery">
    <!-- PREVIEW -->
    <div
      class="DetailImageGallery_Preview"
      :style="getBackgroundImage(activeImage)"
      @click="toggleLargePreview">
      <iframe
        v-if="activeImage.type === 'video'"
        class="_iframe"
        width="100%"
        height="100%"
        :src="computedVideoSrc(activeImage.src)"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen>
      </iframe>
      <button
        v-if="images.length > 1"
        class="CarouselArrow CarouselArrow-left"
        @click.stop="goToImage(activeImageIndex + -1)">
      </button>
      <button
        v-if="images.length > 1"
        class="CarouselArrow CarouselArrow-right"
        @click.stop="goToImage(activeImageIndex + 1)">
      </button>
    </div>

    <!-- THUMBNAILS -->
    <div
      class="DetailImageGallery_Thumbnails">
      <div
        class="_label">
        <h4>
          Previews
        </h4>
        <p
          v-if="filteredVideos.length">
          {{ computedThumbnailsCount.videos }}
        </p>
        <p
          v-if="filteredImages.length"
          class="pb-1">
          {{ computedThumbnailsCount.images }}
        </p>
      </div>
      <button
        class="DetailImageGallery_Thumbnail"
        v-for="(image, imageIndex) in images"
        :class="{
          'active': imageIndex === activeImageIndex,
          '_video': image.type === 'video',
        }"
        :src="image.thumbnail"
        alt="App Thumbnail"
        :key="image.thumbnail + imageIndex"
        :style="getBackgroundImage(image, true)"
        @click="activeImageIndex = imageIndex">
      </button>
    </div>

    <!-- LARGE PREVIEW -->
    <div
      v-if="largePreviewOpen"
      class="DetailImageGallery_LargePreview"
      :class="{ largePreviewOpen }"
      @click="toggleLargePreview">
      <img
        v-if="activeImage.type === 'image'"
        class="LargePreview_image"
        :src="activeImage.src"
        alt="App Screenshot"
      />
      <iframe
        v-if="activeImage.type === 'video'"
        class="_iframe LargePreview_iframe"
        width="100%"
        height="100%"
        :src="computedVideoSrc(activeImage.src)"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen>
      </iframe>
      <button
        class="LargePreview_exit">
      </button>
      <button
        v-if="images.length > 1"
        class="CarouselArrow CarouselArrow-left"
        @click.stop="goToImage(activeImageIndex + -1)">
      </button>
      <button
        v-if="images.length > 1"
        class="CarouselArrow CarouselArrow-right"
        @click.stop="goToImage(activeImageIndex + 1)">
      </button>
    </div>
  </div>
</template>

<script>
import StringUtilsMixin from '@/mixins/string-utils';

export default {
  name: 'DetailImageGallery',

  mixins: [
    StringUtilsMixin,
  ],

  data() {
    return {
      activeImageIndex: 0,
      largePreviewOpen: false,
    };
  },

  props: {
    images: { type: Array, required: true }, // {src, thumbnail, type}
  },

  computed: {
    activeImage() {
      return this.images[this.activeImageIndex];
    },

    filteredImages() {
      return this.images.filter((i) => i.type === 'image');
    },

    filteredVideos() {
      return this.images.filter((i) => i.type === 'video');
    },

    computedThumbnailsCount() {
      return {
        images: `${this.filteredImages.length} ${this.pluralize('image', this.filteredImages.length)}`,
        videos: `${this.filteredVideos.length} ${this.pluralize('video', this.filteredVideos.length)}`,
      };
    },
  },

  deactivated() {
    document.removeEventListener('keyup', this.handleKeyup);
  },

  mounted() {
    document.addEventListener('keyup', this.handleKeyup);
  },

  beforeDestroy() {
    document.removeEventListener('keyup', this.handleKeyup);
  },

  methods: {
    handleKeyup(e) {
      const isEscapeKey = e.which === 27;

      const isLeftArrow = e.which === 37;
      const isRightArrow = e.which === 39;

      if (isEscapeKey && this.largePreviewOpen) {
        this.toggleLargePreview();
      }

      if (isLeftArrow) {
        this.goToImage(this.activeImageIndex + -1);
      }

      if (isRightArrow) {
        this.goToImage(this.activeImageIndex + 1);
      }
    },

    getBackgroundImage(image, isThumbnail = false) {
      if (image.type === 'video' && !isThumbnail) return '';

      return {
        backgroundImage: `url('${isThumbnail ? image.thumbnail : image.src}')`,
      };
    },

    computedVideoSrc(src) {
      return `https://www.youtube.com/embed/${src}`;
    },

    goToImage(imageIndex) {
      if (imageIndex === this.activeImageIndex) return;

      if (imageIndex < 0) {
        this.activeImageIndex = this.images.length - 1;
        return;
      }

      if (imageIndex >= this.images.length) {
        this.activeImageIndex = 0;
        return;
      }

      this.activeImageIndex = imageIndex;
    },

    toggleLargePreview() {
      this.largePreviewOpen = !this.largePreviewOpen;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

.DetailImageGallery {
  display: flex;
}

.DetailImageGallery_Preview {
  flex: 1 1 auto;
  position: relative;
  padding-top: 46%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  cursor: pointer;
  ._iframe {
    position: absolute;
    top: 0;
    left: 0;
  }
  &:hover {
    box-shadow: 0 2px 8px 4px rgba($black, .1), 0 1px 1px 0 rgba($black, .05);
  }
}

.DetailImageGallery_Thumbnails {
  flex: 0 1 140px;
  padding-left: 16px;
  ._label {
    padding-left: 8px;
  }
}

.DetailImageGallery_Thumbnail {
  position: relative;
  width: 52px;
  height: 52px;
  margin: 0 0 8px 8px;
  border: 1px solid rgba($black, .2);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  &.active {
    border: 2px solid $light-blue;
  }
  &:hover {
    box-shadow: 0 2px 8px 4px rgba($black, .1), 0 1px 1px 0 rgba($black, .05);
  }
}

.DetailImageGallery_Thumbnail._video {
  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
  }
  &:before {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: rgba($black, .6);
  }
  &:after {
    $size: 10px;
    left: 2px;
    width: $size;
    height: $size;
    border-left: ($size - 1) solid $white;
    border-top: ($size / 2) solid transparent;
    border-bottom: ($size / 2) solid transparent;
  }
}

.DetailImageGallery_Preview {
  .CarouselArrow {
    opacity: 0;
  }
  &:hover .CarouselArrow {
    opacity: .7;
  }
}

.CarouselArrow {
  display: block;
  position: absolute;
  z-index: 1;
  width: 32px;
  height: 32px;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  border-radius: 50%;
  background-color: $white;
  opacity: .7;
  box-shadow: 0 1px 8px rgba($black, .1);
  cursor: pointer;
  will-change: opacity;
  transition: opacity .2s ease 0s;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 12px;
    height: 12px;
    margin: auto;
    border-top: 2px solid $dark;
    border-left: 2px solid $dark;
  }
}

.CarouselArrow-left {
  left: 8px;
  &:after { right: -4px; transform: rotate(-45deg); }
}

.CarouselArrow-right {
  right: 8px;
  &:after { left: -4px; transform: rotate(135deg); }
}

.DetailImageGallery_LargePreview {
  display: none;
  z-index: 2;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 64px 8%;
  align-items: top;
  justify-content: center;
  background-color: rgba($black, .7);
  overflow-y: scroll;
  &.largePreviewOpen {
    display: flex;
  }
  .CarouselArrow {
    position: fixed;
  }
  .CarouselArrow-left {
    left: 40px;
  }

  .CarouselArrow-right {
    right: 40px;
  }
  &:before {
    content: '';
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 60px;
    background: linear-gradient(to bottom, $black, rgba($black, 0));
  }
}

.LargePreview_exit {
  position: fixed;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  &:hover {
    background-color: rgba($white, .2);
  }
  &:before,
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 2px;
    height: 20px;
    margin: auto;
    background-color: $white;
  }
  &:before {
    transform: rotate(45deg);
  }
  &:after {
    transform: rotate(-45deg);
  }
}

.LargePreview_image {
  display: block;
  width: 100%;
  max-width: 1100px;
  margin: auto;
}

.LargePreview_iframe {
  max-width: 890px;
  max-height: 500px;
  margin: auto;
}

@media only screen and (max-width: 800px) {
  .LargePreview_iframe {
    max-height: 400px;
  }
}

@media only screen and (max-width: 500px) {
  .DetailImageGallery {
    flex-wrap: wrap;
  }

  .DetailImageGallery_Preview,
  .DetailImageGallery_Thumbnails {
    flex: 0 1 100%;
  }

  .DetailImageGallery_Thumbnails {
    padding-top: 16px;
    padding-left: 0;
    margin-left: -8px;
    ._label {
      display: none;
    }
  }
}
</style>
